import { IStorePackageVariation } from 'models/store/i-store-package-variation.interface';
import { IStorePackage } from 'models/store/i-store-package.interface';

import { PackageImage } from 'components/packageImage/PackageImage';

import { useCurrencyWithBalance } from 'hooks/useCurrency';
import { useDecimalPrice } from 'hooks/useDecimalPrice';
import { useEnv } from 'hooks/useEnv';

import Button from '../../buttons/Button';
import ElementLoader from '../../element-loader/ElementLoader';
import ValueAndIcon from '../../generics/ValueAndIcon';
import CheckoutBalance from '../checkoutBalance/CheckoutBalance';
import CheckoutProgressSection from '../completeCheckoutModal/checkoutProgressSection/CheckoutProgressSection';
import './CompletePackageCheckoutOfferModal.scss';

type PurchasePackageCheckoutProps = {
  confirmationPending: boolean;
  packageDetails: IStorePackage;
  selectedOffer: IStorePackageVariation;
  buyPackage: () => void;
};

const PurchasePackageCheckout = ({
  confirmationPending,
  buyPackage,
  packageDetails,
  selectedOffer,
}: PurchasePackageCheckoutProps) => {
  const { royaltiesFee, tradeFee } = useEnv();
  const currency = useCurrencyWithBalance(packageDetails.currency.id);
  const price = Number(selectedOffer.price);
  const royaltiesFeeAmount = useDecimalPrice((royaltiesFee * price) / 100, packageDetails.currency.id);
  const tradeFeeCalculated = useDecimalPrice((tradeFee * price) / 100, packageDetails.currency.id);
  const totalPrice = useDecimalPrice(price, packageDetails.currency.id);

  return (
    <div className="package-checkout-modal__wrapper">
      <div className="package-checkout-modal__content">
        <div className="package-checkout-modal__information-card">
          <div className="tw-flex tw-items-center tw-justify-center">
            <PackageImage
              src={packageDetails.image_url}
              alt={packageDetails.name}
              totalShares={packageDetails.total_shares}
              wrapperClassName="tw-max-w-[243px]"
              countClassName="tw-text-[29px] tw-leading-[28.6px]"
              countSubtitleClassName="tw-text-[12px] tw-font-semibold"
            />
          </div>
          <div className="package-checkout-modal__information-card-details">
            <p className="package-checkout-modal__caption-name tw-text-center lg:tw-text-left">{packageDetails.name}</p>
            <ul className="package-checkout-modal__payment-list">
              <li className="package-checkout-modal__payment-item">
                {currency && (
                  <ElementLoader>
                    <CheckoutBalance currency={currency} />
                  </ElementLoader>
                )}
              </li>
              <li className="package-checkout-modal__payment-item">
                <span>Price per package</span>
                <div className="package-checkout-modal__payment-item-value">
                  <ValueAndIcon
                    valueSize={16}
                    iconSize={16}
                    valueToFixed={false}
                    icon={currency?.icon}
                    value={totalPrice.toString()}
                  />
                </div>
              </li>
              <li className="package-checkout-modal__payment-item">
                <span>Total packages</span>
                <div className="package-checkout-modal__payment-item-value">
                  <ValueAndIcon
                    valueSize={16}
                    // iconSize={16}
                    valueToFixed={false}
                    // icon={currency?.icon}
                    value={selectedOffer.amount_multiplier.toString()}
                  />
                </div>
              </li>
              <li className="package-checkout-modal__payment-item">
                <span>Royalties fee {royaltiesFee} %</span>
                <div className="package-checkout-modal__payment-item-value">
                  <ValueAndIcon
                    valueSize={16}
                    iconSize={16}
                    valueToFixed={false}
                    icon={currency?.icon}
                    value={royaltiesFeeAmount}
                  />
                </div>
              </li>
              <li className="package-checkout-modal__payment-item">
                <span>Trade fee {tradeFee} %</span>
                <div className="package-checkout-modal__payment-item-value">
                  <ValueAndIcon
                    valueSize={16}
                    iconSize={16}
                    valueToFixed={false}
                    icon={currency?.icon}
                    value={tradeFeeCalculated}
                  />
                </div>
              </li>
              <li className="package-checkout-modal__payment-item package-checkout-modal__payment-summary">
                <span>You will pay total</span>
                <div className="package-checkout-modal__payment-item-value">
                  <ValueAndIcon valueSize={24} iconSize={24} icon={currency?.icon} value={totalPrice} />
                </div>
              </li>
            </ul>
          </div>
        </div>
        {confirmationPending && <CheckoutProgressSection title="Confirm purchase" />}
        <Button
          type="button"
          color="primary"
          className="package-checkout-modal__button"
          text={confirmationPending ? 'waiting for approval...' : 'complete purchase'}
          disabled={confirmationPending}
          onClick={buyPackage}
        ></Button>
      </div>
    </div>
  );
};

export default PurchasePackageCheckout;
